import {
    Button,
    Popover,
    PopoverContent,
    PopoverTrigger,
    ScrollArea,
    Separator,
    cn,
} from '@repo/shadcn';
import { formatDistance } from 'date-fns';
import { Bell, CheckCheck, Loader2Icon, RefreshCcw } from 'lucide-react';

import { useRecentNotifications } from '@/modules/notification/recent-notifications/hook';

type NotificationItemProps = {
    item: any;
};

type NotificationTypeProps = {
    type?: string;
};

const NotificationType = ({ type }: NotificationTypeProps) => {
    if (type === 'wholesale_order_placed') {
        return (
            <small className={'text-xs italic text-muted-foreground'}>
                Wholesale Request
            </small>
        );
    }

    return (
        <small className={'text-xs italic text-muted-foreground'}>
            New Notification
        </small>
    );
};

const NotificationItem = ({ item }: NotificationItemProps) => {
    // debugger;

    const time = formatDistance(new Date(item?.created_at), new Date(), {
        addSuffix: false,
    });

    const status = item?.status;

    return (
        <div className={'grid grid-cols-[18px_auto] gap-1'}>
            <div className={'mt-0.5 flex items-start justify-center'}>
                <Bell
                    className={cn(
                        'size-4',
                        status === 'unread' ? 'text-black' : 'text-black/40',
                    )}
                />
            </div>
            <div className={'flex flex-col items-start justify-center'}>
                <p className={'text-justify text-sm'}>{item?.message}</p>

                <p
                    className={
                        'flex w-full grow items-center justify-between gap-3'
                    }
                >
                    <NotificationType type={item?.type} />

                    <small className={'text-xs text-muted-foreground'}>
                        {time}
                    </small>
                </p>
            </div>
            <Separator className={'col-span-2 w-full'} />
        </div>
    );
};

export const RecentNotification = () => {
    const {
        data,
        isFetching,
        isLoading: isNotificationLoading,
        isError,
        isUpdateAllLoading,
        handleUpdateAllAsRead,
        handleRefresh,
    } = useRecentNotifications();

    const totalNotifications = data?.data?.length || 0;
    const isLoading = isNotificationLoading || isFetching;

    const renderTrigger = () => {
        return (
            <Button
                className="relative"
                type={'button'}
                variant="link"
                size={'icon'}
            >
                <Bell className={'size-6 text-black'} />
                {totalNotifications > 0 && (
                    <div
                        className={
                            'absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-red-500'
                        }
                    />
                )}
            </Button>
        );
    };

    const renderHeader = () => {
        return (
            <div className={'flex items-start justify-between'}>
                <h3 className={'text-xl font-medium'}>
                    Notifications (
                    {isLoading ? (
                        <Loader2Icon className={'size-4 animate-spin'} />
                    ) : (
                        totalNotifications
                    )}
                    )
                </h3>

                <div className="flex items-center gap-2">
                    <Button
                        variant={'ghost'}
                        className={
                            'm-0 cursor-pointer p-0 duration-150 hover:bg-transparent hover:text-black hover:underline disabled:cursor-not-allowed'
                        }
                        disabled={isLoading}
                        // size={'icon'}
                        onClick={handleRefresh}
                    >
                        {isUpdateAllLoading ? (
                            <Loader2Icon className={'size-4 animate-spin'} />
                        ) : (
                            <RefreshCcw className={'size-4'} />
                        )}
                    </Button>

                    <Button
                        variant={'ghost'}
                        className={
                            'm-0 cursor-pointer p-0 duration-150 hover:bg-transparent hover:text-black hover:underline disabled:cursor-not-allowed'
                        }
                        disabled={totalNotifications === 0 || isLoading}
                        // size={'icon'}
                        onClick={handleUpdateAllAsRead}
                    >
                        {isUpdateAllLoading ? (
                            <Loader2Icon className={'size-4 animate-spin'} />
                        ) : (
                            <CheckCheck
                                className={cn(
                                    'size-6',
                                    totalNotifications !== 0 && 'text-primary',
                                )}
                            />
                        )}
                    </Button>
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (isLoading) {
            return <p>Loading...</p>;
        }

        if (isError) {
            return <p className={'text-red-500'}>Something went wrong!</p>;
        }

        if (totalNotifications === 0) {
            return <p className={'text-center'}>No notifications found.</p>;
        }

        return data?.data.map((notification) => (
            <div key={notification.id} className={'space-y-2'}>
                <NotificationItem item={notification} />
            </div>
        ));
    };

    return (
        <Popover>
            <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
            <PopoverContent className="w-fit min-w-72 max-w-96" align={'end'}>
                {renderHeader()}
                <ScrollArea className={'h-[50vh] w-full pt-4'}>
                    <div className={'space-y-4'}>
                        <div className={'space-y-2'}>{renderContent()}</div>
                    </div>
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
};

RecentNotification.displayName = 'RecentNotification';
